import { useState } from 'react';
import type { Recommendation, ThresholdState } from '../../netlify/functions/_lib/domain.ts';
import Button from './Button.tsx';
import { Field, TextArea } from './Field.tsx';
import { ThresholdWord } from './StatusWord.tsx';

export default function EvidencePanel({
  recommendation,
  onConfirm,
}: {
  recommendation: Recommendation;
  onConfirm: (payload: {
    summary: string;
    next_step: string;
    evidence: Recommendation['evidence'];
    rule_id: Recommendation['ruleId'];
    threshold_state: ThresholdState;
  }) => Promise<void>;
}) {
  const [summary, setSummary] = useState(recommendation.summary);
  const [nextStep, setNextStep] = useState(recommendation.nextStep);
  const [error, setError] = useState('');
  const [pending, setPending] = useState(false);

  async function save() {
    setError('');
    setPending(true);
    try {
      await onConfirm({
        summary,
        next_step: nextStep,
        evidence: recommendation.evidence,
        rule_id: recommendation.ruleId,
        threshold_state: recommendation.thresholdState,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save that decision.');
    } finally {
      setPending(false);
    }
  }

  return (
    <section className="rounded-xl border border-border bg-surface p-6 shadow-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xl">Suggested next step</h2>
        <ThresholdWord state={recommendation.thresholdState} />
      </div>
      <p className="mt-2 text-sm text-ink-subtle">
        The engine suggests. You confirm or edit before anything is saved.
      </p>

      <Field label="Recommendation">
        <TextArea value={summary} onChange={(e) => setSummary(e.target.value)} maxLength={2000} />
      </Field>
      <div className="mt-4">
        <Field label="Next step">
          <TextArea value={nextStep} onChange={(e) => setNextStep(e.target.value)} maxLength={2000} />
        </Field>
      </div>

      <h3 className="mt-6 text-base">Evidence</h3>
      <ul className="mt-2 divide-y divide-border rounded border border-border">
        {recommendation.evidence.map((item) => (
          <li key={item.label} className="flex flex-col gap-1 px-3 py-2 sm:flex-row sm:justify-between">
            <span className="text-sm text-ink-muted">{item.label}</span>
            <span className="font-mono text-sm">{item.value}</span>
          </li>
        ))}
      </ul>

      {error ? <p className="mt-3 text-sm text-danger">{error}</p> : null}
      <div className="mt-5">
        <Button disabled={pending} onClick={() => void save()}>
          Confirm and save this decision
        </Button>
      </div>
    </section>
  );
}
